import React, { useEffect, useState } from "react";
import "react-alice-carousel/lib/alice-carousel.css";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import styled from "styled-components";
import properties from "../../../apiConfigs/properties";
import { ReactComponent as Seperate } from "../../../assets/seperator.svg";
import { useRoot } from "../../../context/RootProvider";
import Button from "../../../designComponents/Button";
import DeleteModal from "../../../designComponents/DeleteModal";
import Loading from "../../../designComponents/Loading";
import { PROPERTIES_ROUTE } from "../../../routes/routePaths";
import requestAPI from "../../../utils/requestAPI";
import { uploadFilesToS3OnlyKeys } from "../../../utils/s3Upload";
import AmenitiesCard from "./AmenitiesCard";
import GeneralInformation from "./GeneralInformation";
import History from "./History";
import ImageCards from "./ImageCards";
import InventoryCards from "./InventoryCards";
import Notificationsection from "./Notificationsection";
import RefImages from "./RefImages";
import AcceptProperty from "./review/AcceptProperty";
import CreateReview from "./review/CreateReview";
import ReviewSummary from "./review/ReviewSummary";
import ViewReviews from "./review/ViewReviews";
import RulesCard from "./RulesCard";
import {
  ActiveNavText,
  ButtonsSection,
  Container,
  FlexContainer,
  LeftColumn,
  Navigation,
  NavLink,
  NavText,
  RightColumn,
  Title,
  TitleSection,
  Wrapper,
} from "./styles";
import {
  OldReviewsType,
  PropertyDetailsType,
  ReviewFieldNameType,
  ReviewReport,
  ReviewReportSend,
  ViewReviewType,
} from "./types";
import UnitsCard from "./UnitsCard";
import UtilityProviderCard from "./UtilityProviderCard";

export const DetailsWrapperStyled = styled.div`
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const InfoRowStyled = styled.div`
  display: flex;
  /* gap: 60px; */
  align-items: center;
`;

type Props = {};

const PropertyDetails: React.FC<Props> = () => {
  const navigate = useNavigate();

  const [composedNewReview, setComposedNewReview] = useState<
    ReviewReportSend[]
  >([]);
  const [oldReviews, setOldReviews] = useState<OldReviewsType | null>();
  const hasRecentReview = !!oldReviews?.review;
  const hasOlderReview = !!oldReviews?.previousReview;
  const [enabledReviewField, setEnabledReviewField] =
    useState<ReviewFieldNameType>();

  const [reviewSummary, setReviewSummary] = useState<boolean>(false);

  const { propertyId } = useParams();
  const [propertyDetail, setPropertyDetail] = useState<PropertyDetailsType>();

  const [
    enableAcceptPropertyConfirmation,
    setEnableAcceptPropertyConfirmation,
  ] = useState(false);

  const [loading, setLoading] = useState<{
    fetchingData?: boolean;
    fetchingHistory?: boolean;
    sendingReview?: boolean;
    deleting?: boolean;
    accepting?: boolean;
  }>({});
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [viewReview, setViewReview] = useState<{
    field: ReviewFieldNameType;
    reviews: ReviewReport[];
    status: boolean;
    createdAt: string;
  }>({ field: undefined, reviews: [], status: false, createdAt: "" });

  const isComplex = propertyDetail?.property?.isComplex;
  const isProperty = !isComplex;

  const handleDelete = async () => {
    try {
      await requestAPI(properties.deleteProperty(propertyId));
      toast.success(`Sucessfully  deleted property`);
      navigate(PROPERTIES_ROUTE);
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const handleAccept = async () => {
    setLoading((prev) => ({ ...prev, accepting: true }));
    try {
      await requestAPI(properties.publishProperty(oldReviews?._id));
      toast.success(`Sucessfully  accepted property`);
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      getPropertyDetails(propertyId as string);
      setLoading((prev) => ({ ...prev, accepting: false }));
      setEnableAcceptPropertyConfirmation(false);
    }
  };

  const handleSendReview = async () => {
    try {
      if (composedNewReview?.length === 0) {
        toast.error("There are no any reviews to send.");
        return;
      }

      setLoading((prev) => ({ ...prev, sendingReview: true }));

      const fieldImagesMap = new Map<string, string[]>();
      const filteredComposedFields = composedNewReview?.filter(
        (cnr) => cnr?.filesFile && cnr?.filesFile?.length > 0
      );
      if (filteredComposedFields?.length > 0) {
        await Promise.all(
          filteredComposedFields?.map(async (review) => {
            const files = review?.filesFile?.map((image) => image.file);
            if (files && files?.length && oldReviews?._id && review.field) {
              const uploadFiles = await uploadFilesToS3OnlyKeys({
                category: "review",
                fileUri: files,
                reviewId: oldReviews?._id ?? "",
              });

              if (uploadFiles && uploadFiles?.length > 0) {
                fieldImagesMap.set(review.field, uploadFiles);
              }
            }
          })
        );
      }

      const reviews: ReviewReportSend[] = composedNewReview
        ?.filter((cReview) => {
          if (!cReview?.field) {
            return false;
          }
          if (
            isComplex &&
            cReview?.field &&
            ["Rules", "Inventory", "RefRoom"].includes(cReview?.field)
          ) {
            return false;
          }
          return true;
        })
        ?.map((cReview) => {
          return {
            description: cReview.description,
            status: cReview.status,
            field: cReview.field,
            files: cReview.field && fieldImagesMap.get(cReview.field),
          };
        })
        .filter(Boolean);

      await requestAPI(properties.reviewProperty(oldReviews?._id, reviews));
      toast.success(`Reviewed successfully `);
      setReviewSummary(false);
      setComposedNewReview([]);

      getPropertyReviewDetails(propertyId ?? "");
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading((prev) => ({ ...prev, sendingReview: false }));
    }
  };
  const getPropertyReviewDetails = async (id: string) => {
    setLoading((prev) => ({ ...prev, fetchingHistory: true }));
    try {
      const reviews = await requestAPI(properties.getPropertyReview(id));
      setOldReviews(reviews);

      setLoading((prev) => ({ ...prev, fetchingHistory: false }));
    } catch (error) {
      setLoading((prev) => ({ ...prev, fetchingHistory: false }));
    }
  };

  const getPropertyDetails = async (id: string) => {
    setLoading((prev) => ({ ...prev, fetchingData: true }));
    try {
      const response = await requestAPI(properties.getSingleProperty(id));
      setPropertyDetail(response);
      setLoading((prev) => ({ ...prev, fetchingData: false }));
    } catch (error) {
      setLoading((prev) => ({ ...prev, fetchingData: false }));
    }
  };
  useEffect(() => {
    if (propertyId) {
      getPropertyDetails(propertyId);
      getPropertyReviewDetails(propertyId);
    }
  }, [propertyId]);

  const handleClickEdit = (field: ReviewFieldNameType) => {
    setReviewSummary(false);
    setEnabledReviewField(field);
  };
  const { auth } = useRoot();

  return (
    <Wrapper>
      {loading?.fetchingData ? (
        <Loading />
      ) : (
        <>
          <Container
            style={{
              position: "sticky",
              top: 75,
              background: "#f9f9fd",
              zIndex: 100,
              padding: "10px 0px",
            }}
          >
            <TitleSection>
              <Title>Property {isComplex ? "Complex" : ""} Details</Title>
              <Navigation
                style={{
                  paddingTop: 13,
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                <NavLink onClick={() => navigate(PROPERTIES_ROUTE)}>
                  <NavText>Table</NavText>
                </NavLink>
                <Seperate fill="#7E7E7E" />

                <ActiveNavText>Property details</ActiveNavText>
              </Navigation>
            </TitleSection>
            <ButtonsSection>
              {hasRecentReview && (
                <Button
                  variant="gray"
                  bgColor="highlight"
                  color="lightGrey.ivory"
                  onClick={() => {
                    setViewReview({
                      status: true,
                      field: undefined,
                      reviews: oldReviews?.review || [],
                      createdAt: oldReviews?.createdAt,
                    });
                  }}
                  isLoading={loading?.accepting}
                  isDisabled={loading?.accepting}
                >
                  View Reviews
                </Button>
              )}
              {hasOlderReview && (
                <Button
                  variant="gray"
                  bgColor="dark.neutral_94"
                  color="lightGrey.ivory"
                  onClick={() => {
                    setViewReview({
                      status: true,
                      field: undefined,
                      reviews: oldReviews?.previousReview?.review || [],
                      createdAt: oldReviews?.previousReview?.createdAt,
                    });
                  }}
                  isLoading={loading?.accepting}
                  isDisabled={loading?.accepting}
                >
                  Previous Reviews
                </Button>
              )}
              {propertyDetail?.property?.status === "UNDER_REVIEW" && (
                <>
                  <Button
                    variant="gray"
                    bgColor="secondary"
                    color="lightGrey.ivory"
                    onClick={() => {
                      setEnableAcceptPropertyConfirmation(true);
                    }}
                    isLoading={loading?.accepting}
                    isDisabled={loading?.accepting}
                  >
                    Accept property
                  </Button>
                  <Button
                    variant="gray"
                    bgColor="status.success"
                    color="lightGrey.ivory"
                    onClick={() => {
                      setReviewSummary(true);
                    }}
                  >
                    Submit Reviews [{composedNewReview?.length}]
                  </Button>
                </>
              )}
            </ButtonsSection>
          </Container>

          <CreateReview
            fieldName={enabledReviewField}
            onClose={() => {
              setEnabledReviewField(undefined);
            }}
            visible={!!enabledReviewField}
            reviewsData={composedNewReview}
            setReviewsData={setComposedNewReview}
          />
          <Container style={{ padding: 0 }}>
            <FlexContainer>
              <LeftColumn>
                <GeneralInformation details={propertyDetail} />

                {propertyDetail && (
                  <ImageCards
                    isAccepted={
                      oldReviews?.review?.find((obj) => obj.field === "Images")
                        ?.status === "ACCEPTED"
                        ? true
                        : oldReviews?.review?.find(
                            (obj) => obj.field === "Images"
                          )?.status === "REJECTED"
                        ? false
                        : null
                    }
                    propertyDetail={propertyDetail}
                    setOpenReviewModal={setEnabledReviewField}
                    hasPreviousReview={
                      hasOlderReview &&
                      oldReviews?.previousReview?.review?.some(
                        (x) => x.field === "Images"
                      )
                    }
                    hasRecentReview={
                      hasRecentReview &&
                      oldReviews?.review?.some((x) => x.field === "Images")
                    }
                    onViewReviews={(type: ViewReviewType) => {
                      setViewReview({
                        status: true,
                        field: "Images",
                        reviews:
                          type === "recent"
                            ? oldReviews?.review || []
                            : oldReviews?.previousReview?.review || [],
                        createdAt:
                          type === "recent"
                            ? oldReviews?.createdAt || ""
                            : oldReviews?.previousReview?.createdAt || "",
                      });
                    }}
                  />
                )}
                {isProperty && propertyDetail?.property?.utilityProviders && (
                  <UtilityProviderCard
                    utilityProviders={
                      propertyDetail?.property?.utilityProviders
                    }
                  />
                )}

                {isProperty &&
                  propertyDetail &&
                  propertyDetail?.property?.units.length > 0 && (
                    <UnitsCard units={propertyDetail?.property?.units} />
                  )}

                {isProperty && propertyDetail?.property?.refImageGroups && (
                  <RefImages
                    propertyDetail={propertyDetail}
                    isAccepted={
                      oldReviews?.review?.find((obj) => obj.field === "RefRoom")
                        ?.status === "ACCEPTED"
                        ? true
                        : oldReviews?.review?.find(
                            (obj) => obj.field === "RefRoom"
                          )?.status === "REJECTED"
                        ? false
                        : null
                    }
                    setOpenReviewModal={setEnabledReviewField}
                    hasPreviousReview={
                      hasOlderReview &&
                      oldReviews?.previousReview?.review?.some(
                        (x) => x.field === "RefRoom"
                      )
                    }
                    hasRecentReview={
                      hasRecentReview &&
                      oldReviews?.review?.some((x) => x.field === "RefRoom")
                    }
                    onViewReviews={(type: ViewReviewType) => {
                      setViewReview({
                        status: true,
                        field: "RefRoom",
                        reviews:
                          type === "recent"
                            ? oldReviews?.review || []
                            : oldReviews?.previousReview?.review || [],
                        createdAt:
                          type === "recent"
                            ? oldReviews?.createdAt || ""
                            : oldReviews?.previousReview?.createdAt || "",
                      });
                    }}
                  />
                )}
                {isProperty && propertyDetail && (
                  <RulesCard
                    isAccepted={
                      oldReviews?.review?.find((obj) => obj.field === "Rules")
                        ?.status === "ACCEPTED"
                        ? true
                        : oldReviews?.review?.find(
                            (obj) => obj.field === "Rules"
                          )?.status === "REJECTED"
                        ? false
                        : null
                    }
                    propertyDetail={propertyDetail}
                    setOpenReviewModal={setEnabledReviewField}
                    hasPreviousReview={
                      hasOlderReview &&
                      oldReviews?.previousReview?.review?.some(
                        (x) => x.field === "Rules"
                      )
                    }
                    hasRecentReview={
                      hasRecentReview &&
                      oldReviews?.review?.some((x) => x.field === "Rules")
                    }
                    onViewReviews={(type: ViewReviewType) => {
                      setViewReview({
                        status: true,
                        field: "Rules",
                        reviews:
                          type === "recent"
                            ? oldReviews?.review || []
                            : oldReviews?.previousReview?.review || [],
                        createdAt:
                          type === "recent"
                            ? oldReviews?.createdAt || ""
                            : oldReviews?.previousReview?.createdAt || "",
                      });
                    }}
                  />
                )}
                {propertyDetail && (
                  <AmenitiesCard
                    isAccepted={
                      oldReviews?.review?.find(
                        (obj) => obj.field === "Amenities"
                      )?.status === "ACCEPTED"
                        ? true
                        : oldReviews?.review?.find(
                            (obj) => obj.field === "Amenities"
                          )?.status === "REJECTED"
                        ? false
                        : null
                    }
                    propertyDetail={propertyDetail}
                    setOpenReviewModal={setEnabledReviewField}
                    hasPreviousReview={
                      hasOlderReview &&
                      oldReviews?.previousReview?.review?.some(
                        (x) => x.field === "Amenities"
                      )
                    }
                    hasRecentReview={
                      hasRecentReview &&
                      oldReviews?.review?.some((x) => x.field === "Amenities")
                    }
                    onViewReviews={(type: ViewReviewType) => {
                      setViewReview({
                        status: true,
                        field: "Amenities",
                        reviews:
                          type === "recent"
                            ? oldReviews?.review || []
                            : oldReviews?.previousReview?.review || [],
                        createdAt:
                          type === "recent"
                            ? oldReviews?.createdAt || ""
                            : oldReviews?.previousReview?.createdAt || "",
                      });
                    }}
                  />
                )}

                {isProperty && propertyDetail && (
                  <InventoryCards
                    isAccepted={
                      oldReviews?.review?.find(
                        (obj) => obj.field === "Inventory"
                      )?.status === "ACCEPTED"
                        ? true
                        : oldReviews?.review?.find(
                            (obj) => obj.field === "Inventory"
                          )?.status === "REJECTED"
                        ? false
                        : null
                    }
                    propertyDetail={propertyDetail}
                    setOpenReviewModal={setEnabledReviewField}
                    hasPreviousReview={
                      hasOlderReview &&
                      oldReviews?.previousReview?.review?.some(
                        (x) => x.field === "Inventory"
                      )
                    }
                    hasRecentReview={
                      hasRecentReview &&
                      oldReviews?.review?.some((x) => x.field === "Inventory")
                    }
                    onViewReviews={(type: ViewReviewType) => {
                      setViewReview({
                        status: true,
                        field: "Inventory",
                        reviews:
                          type === "recent"
                            ? oldReviews?.review || []
                            : oldReviews?.previousReview?.review || [],
                        createdAt:
                          type === "recent"
                            ? oldReviews?.createdAt || ""
                            : oldReviews?.previousReview?.createdAt || "",
                      });
                    }}
                  />
                )}
              </LeftColumn>
              <RightColumn>
                <Container
                  style={{
                    padding: "10px 0px",
                  }}
                >
                  {auth?.role === "ADMIN" && (
                    <Button
                      variant="danger"
                      radius="normal"
                      onClick={() => setDeleteModal(true)}
                    >
                      Delete Property
                    </Button>
                  )}
                  <History />
                </Container>
                <Notificationsection />
              </RightColumn>
              <DeleteModal
                title="Are you sure you want to delete?"
                onClose={() => {
                  setDeleteModal(false);
                }}
                loading={!!loading?.deleting}
                isOpen={deleteModal}
                itemName={"this Property"}
                onDelete={() => {
                  handleDelete();
                }}
              />
            </FlexContainer>
          </Container>
        </>
      )}

      <ReviewSummary
        handleSubmitReviews={handleSendReview}
        handleClickEdit={handleClickEdit}
        reportReview={composedNewReview}
        onClose={() => {
          setReviewSummary(false);
        }}
        visible={reviewSummary}
        submitting={loading.sendingReview}
      />

      <ViewReviews
        isVisible={viewReview?.status}
        onClose={() => {
          setViewReview({
            field: undefined,
            reviews: [],
            status: false,
            createdAt: "",
          });
        }}
        reviews={viewReview?.reviews}
        field={viewReview?.field}
        createdAt={viewReview.createdAt}
      />
      <AcceptProperty
        recentReviews={oldReviews?.review || []}
        onAccept={handleAccept}
        isVisible={enableAcceptPropertyConfirmation}
        onClose={() => setEnableAcceptPropertyConfirmation(false)}
        loading={!!loading.accepting}
      />
    </Wrapper>
  );
};

export default PropertyDetails;
